import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Button, Card, Col, Form, Modal, Row, Spinner } from 'react-bootstrap';
import moment from 'moment';
import { store } from '../../../../store/store';
import ManagerSubscriptionsApiClient from '../../../../api-client/ManagerSubscriptionsApiClient';
import { conMotivo } from '../../../components/Util/erroreServer';
import { useStatoNelTitolo } from '../../../components/Util/StatoProdotto';
import { usePageTitle } from '../../../layouts/PageHeaderExtra';
import { apriFile } from '../../../components/Util/anteprimaFile';
import segnalaMancanti from '../../../components/Util/campiMancanti';

/*
 * Scheda dell'abbonamento di una struttura al gestionale (03/10/2026). Prima si apriva in una finestra
 * sopra la lista «Elenco Abbonamenti», con le azioni sulla fattura; ora è una pagina:
 * stato accanto al titolo (si salva subito), data di fine e importo, dati di fatturazione (modificabili
 * quando lo SDI ha scartato la fattura), «Invia allo SDI», «Scarica XML» e «Visualizza PDF».
 * Non c'è un'API per leggere un abbonamento per id: si rilegge l'elenco e lo si cerca per id
 * (intanto si mostra la riga arrivata dalla lista).
 */

const LIST_URL = '/superadmin/manager-subscriptions';

const STATI = [
    { value: 'active', label: 'Attivo', tono: 'attivo' },
    { value: 'free', label: 'Gratuito', tono: 'attivo' },
    { value: 'expired', label: 'Scaduto', tono: 'non-attivo' },
    { value: 'inactive', label: 'Inattivo', tono: 'non-attivo' },
    { value: 'refunded', label: 'Rimborsato', tono: 'neutro' },
];

const STATI_FATTURA = {
    SENT: 'Inviata',
    ACCEPTED: 'Accettata',
    DELIVERED: 'Consegnata',
    REJECTED: 'Scartata SDI',
    NOT_SENT: 'Da inviare',
    NOT_GENERATED: 'Non generata',
};

const METODI = { card: 'Carta', paypal: 'PayPal', bank_transfer: 'Bonifico' };

const data = (v) => (v ? moment(v).format('DD/MM/YYYY') : '—');

const fatturazioneDa = (sub) => {
    const b = sub?.billingData || {};
    return {
        denominazione: b.denomination || '',
        vatNumber: b.vatNumber || '',
        codeSdi: b.sdiCode || '',
        pec: b.pec || '',
        address: b.address || '',
        zipCode: b.zipCode || '',
        city: b.city || '',
        province: b.province || '',
        country: b.nation || 'IT',
    };
};

const token = () => store.getState().auth.auth.idToken;

const ManagerSubscriptionScheda = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    const [sub, setSub] = useState(location.state?.abbonamento || null);
    const [caricato, setCaricato] = useState(false);
    const [endDate, setEndDate] = useState('');
    const [priceEur, setPriceEur] = useState('');
    const [billing, setBilling] = useState(fatturazioneDa(null));
    const [inCorso, setInCorso] = useState('');
    const [errore, setErrore] = useState('');
    const [confermaSdi, setConfermaSdi] = useState(false);
    const [salvato, setSalvato] = useState(false);

    const riempi = (s) => {
        setSub(s);
        setEndDate(s.endDate ? String(s.endDate).substring(0, 10) : '');
        setPriceEur(s.priceEurCents ? (s.priceEurCents / 100).toFixed(2) : (s.priceEur ? Number(s.priceEur).toFixed(2) : '0'));
        setBilling(fatturazioneDa(s));
    };

    const carica = useCallback(async () => {
        try {
            const res = await new ManagerSubscriptionsApiClient().getAdminSubscriptions(token(), {});
            if (res && !res.error && Array.isArray(res)) {
                const trovato = res.find((s) => String(s.id) === String(id));
                if (trovato) riempi(trovato);
            }
        } catch (e) {
            console.error(e);
        } finally {
            setCaricato(true);
        }
    }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (location.state?.abbonamento) riempi(location.state.abbonamento);
        carica();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [carica]);

    usePageTitle(sub?.clubName);

    // Lo stato si salva subito, con data di fine e importo già salvati
    useStatoNelTitolo({
        visibile: !!sub,
        stati: STATI,
        valore: sub?.status,
        onCambia: async (nuovo) => {
            const res = await new ManagerSubscriptionsApiClient().updateAdminSubscription(token(), sub.id, {
                status: nuovo,
                endDate: sub.endDate || null,
                priceEurCents: sub.priceEurCents ?? Math.round(parseFloat(sub.priceEur || 0) * 100),
            });
            if (res !== true) return { error: res?.error || 'Errore durante il salvataggio' };
            setSub((s) => ({ ...s, status: nuovo }));
            return {};
        },
    });

    const mostraSalvato = () => {
        setSalvato(true);
        setTimeout(() => setSalvato(false), 2500);
    };

    if (!sub) {
        return (
            <>
                <Card>
                    <Card.Body className="sid-card-body--spaced">
                        {caricato ? 'Abbonamento non trovato.' : 'Caricamento in corso...'}
                    </Card.Body>
                </Card>
            </>
        );
    }

    const fattura = sub.invoiceStatus;
    const conFattura = fattura && fattura !== 'NOT_GENERATED';
    const daInviare = fattura === 'NOT_SENT' || fattura === 'REJECTED';
    // I dati di fatturazione si correggono quando lo SDI ha scartato la fattura
    const fatturazioneModificabile = fattura === 'REJECTED';
    const errori = sub.sdiRejectionErrors || sub.sdi_rejection_errors || [];

    const salva = async () => {
        if (fatturazioneModificabile && (billing.vatNumber || '').trim().toUpperCase().startsWith('IT')) {
            segnalaMancanti([{ campo: 'ms-vatNumber', messaggio: 'La partita IVA non deve iniziare con «IT»: scrivi solo le 11 cifre' }]);
            return;
        }
        setInCorso('salva');
        try {
            const client = new ManagerSubscriptionsApiClient();
            const priceEurCents = Math.round(parseFloat(priceEur || 0) * 100);
            const res = await client.updateAdminSubscription(token(), sub.id, {
                status: sub.status,
                endDate: endDate || null,
                priceEurCents,
            });
            if (res !== true) {
                setErrore(res?.error || 'Errore durante il salvataggio');
                return;
            }
            if (fatturazioneModificabile) {
                const resB = await client.updateBillingData(token(), {
                    managerSubscriptionId: sub.id,
                    type: 'business',
                    nation: billing.country,
                    address: billing.address,
                    zipCode: billing.zipCode,
                    city: billing.city,
                    province: billing.province,
                    pec: billing.pec,
                    denomination: billing.denominazione,
                    vatNumber: billing.vatNumber,
                    sdiCode: billing.codeSdi,
                });
                if (resB !== true) {
                    setErrore(resB?.error || 'Errore durante il salvataggio dei dati di fatturazione');
                    return;
                }
            }
            await carica();
            mostraSalvato();
        } catch (e) {
            console.error(e);
            setErrore('Errore di connessione');
        } finally {
            setInCorso('');
        }
    };

    const inviaSdi = async () => {
        setConfermaSdi(false);
        setInCorso('sdi');
        try {
            const res = await new ManagerSubscriptionsApiClient().sdiResendInvoiceAdmin(token(), sub.id);
            if (res === true) await carica();
            else setErrore(res?.error || "Errore durante l'invio allo SDI");
        } catch (e) {
            console.error(e);
        } finally {
            setInCorso('');
        }
    };

    const scaricaPdf = async () => {
        setInCorso('pdf');
        try {
            const res = await new ManagerSubscriptionsApiClient().downloadInvoicePdf(token(), sub.id);
            if (res && res.pdfUrl) {
                // La fattura si vede nella finestra di anteprima, senza scaricarla (set-ui anteprima-file)
                apriFile({ uri: res.pdfUrl, nome: `Fattura_${sub.transactionId || sub.id}.pdf`, tipo: 'application/pdf' });
            } else {
                setErrore(conMotivo('Si è verificato un errore durante il recupero del PDF.'));
            }
        } catch (error) {
            console.error('Errore download fattura', error);
            setErrore(conMotivo('Si è verificato un errore durante il download.'));
        } finally {
            setInCorso('');
        }
    };

    const scaricaXml = async () => {
        setInCorso('xml');
        try {
            const xmlBlob = await new ManagerSubscriptionsApiClient().downloadInvoiceXml(token(), sub.id);
            const url = window.URL.createObjectURL(new Blob([xmlBlob], { type: 'application/xml' }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Fattura_${sub.transactionId || sub.id}.xml`);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Errore download fattura xml', error);
            setErrore(conMotivo('Si è verificato un errore durante il download.'));
        } finally {
            setInCorso('');
        }
    };

    const campoFatturazione = (nome, etichetta, md, tipo = 'text') => (
        <Col md={md}>
            <Form.Label htmlFor={`ms-${nome}`}>{etichetta}</Form.Label>
            {fatturazioneModificabile ? (
                <Form.Control id={`ms-${nome}`} type={tipo} value={billing[nome]} onChange={(e) => setBilling({ ...billing, [nome]: e.target.value })} />
            ) : (
                <div className="sid-readonly-value">{billing[nome] || '—'}</div>
            )}
        </Col>
    );

    const rinnovo = sub.stripeSubscriptionId ? (sub.cancelAtPeriodEnd ? 'Annullato' : 'Automatico') : 'Manuale';
    const metodo = METODI[sub.paymentMethod] || (sub.status === 'free' || !sub.priceEur ? 'Gratuito' : (sub.stripeSubscriptionId ? 'Carta' : '—'));

    return (
        <>
            <Modal className="fade" show={inCorso === 'sdi'} backdrop="static" keyboard={false} centered size="sm">
                <Modal.Body className="text-center py-4">
                    <Spinner animation="border" className="mb-3" />
                    <p className="mb-0">Invio in corso...</p>
                </Modal.Body>
            </Modal>

            <Modal className="fade" show={!!errore} onHide={() => setErrore('')} centered>
                <Modal.Header closeButton><Modal.Title>Errore durante l'operazione</Modal.Title></Modal.Header>
                <Modal.Body>{errore}</Modal.Body>
            </Modal>

            <Modal className="fade" show={confermaSdi} onHide={() => setConfermaSdi(false)} centered>
                <Modal.Header closeButton><Modal.Title>Inviare la fattura allo SDI?</Modal.Title></Modal.Header>
                <Modal.Body>La fattura dell'abbonamento di «{sub.clubName}» verrà inviata allo SDI.</Modal.Body>
                <Modal.Footer className="justify-content-between">
                    <Button className="btn btn-light btn-add" onClick={() => setConfermaSdi(false)}>Annulla</Button>
                    <Button className="btn btn-primary btn-add" onClick={inviaSdi}>Invia</Button>
                </Modal.Footer>
            </Modal>

            <Card>
                <Card.Body className="sid-card-body--spaced">
                    <h5 className="sid-form-section">Struttura</h5>
                    <Row className="g-4">
                        <Col md={4}>
                            <Form.Label>Club</Form.Label>
                            <div className="sid-readonly-value">{sub.clubName || '—'}</div>
                        </Col>
                        <Col md={4}>
                            <Form.Label>Email</Form.Label>
                            <div className="sid-readonly-value">{sub.clubEmail || '—'}</div>
                        </Col>
                        <Col md={4}>
                            <Form.Label>Telefono</Form.Label>
                            <div className="sid-readonly-value">{sub.clubPhone || '—'}</div>
                        </Col>
                    </Row>

                    <h5 className="sid-form-section mt-4">Abbonamento</h5>
                    <Row className="g-4">
                        <Col md={4}>
                            <Form.Label>Data inizio</Form.Label>
                            <div className="sid-readonly-value">{data(sub.startDate)}</div>
                        </Col>
                        <Col md={4}>
                            <Form.Label>Data fine</Form.Label>
                            <Form.Control type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                        </Col>
                        <Col md={4}>
                            <Form.Label>Importo (€)</Form.Label>
                            <Form.Control type="number" step="0.01" value={priceEur} onChange={(e) => setPriceEur(e.target.value)} />
                        </Col>
                        <Col md={4}>
                            <Form.Label>Pagamento</Form.Label>
                            <div className="sid-readonly-value">{metodo}</div>
                        </Col>
                        <Col md={4}>
                            <Form.Label>Rinnovo</Form.Label>
                            <div className="sid-readonly-value">{rinnovo}</div>
                        </Col>
                    </Row>

                    <h5 className="sid-form-section mt-4">Fattura</h5>
                    <Row className="g-4">
                        <Col md={4}>
                            <Form.Label>Num. documento</Form.Label>
                            <div className="sid-readonly-value">{sub.transactionId || '—'}</div>
                        </Col>
                        <Col md={4}>
                            <Form.Label>Stato fattura</Form.Label>
                            <div className="sid-readonly-value">{fattura ? (STATI_FATTURA[fattura] || fattura) : 'Senza fattura'}</div>
                        </Col>
                        {fattura === 'REJECTED' && errori.length > 0 && (
                            <Col md={12}>
                                <Form.Label>Motivi dello scarto</Form.Label>
                                <div className="sid-readonly-value">
                                    {errori.map((err, i) => (
                                        <div key={i}><strong>{err.Codice}</strong>: {err.Descrizione}</div>
                                    ))}
                                </div>
                            </Col>
                        )}
                    </Row>

                    {(sub.billingData || fatturazioneModificabile) && (
                        <>
                            <h5 className="sid-form-section mt-4">Dati di fatturazione</h5>
                            <Row className="g-4">
                                {campoFatturazione('denominazione', 'Denominazione', 12)}
                                {campoFatturazione('vatNumber', 'Partita IVA', 4)}
                                {campoFatturazione('codeSdi', 'Codice SDI', 4)}
                                {campoFatturazione('pec', 'PEC', 4, 'email')}
                                {campoFatturazione('address', 'Indirizzo (via e civico)', 12)}
                                {campoFatturazione('zipCode', 'CAP', 3)}
                                {campoFatturazione('city', 'Città', 4)}
                                {campoFatturazione('province', 'Provincia (sigla)', 2)}
                                {campoFatturazione('country', 'Nazione', 3)}
                            </Row>
                        </>
                    )}
                </Card.Body>
            </Card>

            <div className="d-flex justify-content-end mt-4">
                <div className="d-flex gap-4 flex-wrap justify-content-end">
                    {daInviare && (
                        <button type="button" className="btn btn-dark light" onClick={() => setConfermaSdi(true)}>Invia allo SDI</button>
                    )}
                    {conFattura && (
                        <button type="button" className="btn btn-dark light" onClick={scaricaXml} disabled={inCorso === 'xml'}>
                            {inCorso === 'xml' ? 'Scaricamento...' : 'Scarica XML'}
                        </button>
                    )}
                    {conFattura && (
                        <button type="button" className="btn btn-dark light" onClick={scaricaPdf} disabled={inCorso === 'pdf'}>
                            {inCorso === 'pdf' ? 'Apertura...' : 'Visualizza PDF'}
                        </button>
                    )}
                    <button type="button" className="btn btn-light" onClick={() => navigate(LIST_URL)}>Annulla</button>
                    <Button className="btn btn-primary" onClick={salva} disabled={inCorso === 'salva'}>Salva</Button>
                </div>
            </div>

            {salvato && (
                <div role="status" style={{ position: 'fixed', left: 24, bottom: 24, zIndex: 2000, background: '#111827', color: '#fff', padding: '10px 16px', borderRadius: 8 }}>
                    Modifica salvata
                </div>
            )}
        </>
    );
};

export default ManagerSubscriptionScheda;
